import { Box, Tabs, Tab } from '@mui/material';
import { useEffect, useState } from 'react';
import { PageTitle } from '../../components/PageTitle';
import { AnimatedBox } from '../../components/AnimatedBox';
import { EmptyState } from '../../components/EmptyState';
import { LoadingState } from '../../components/LoadingState';
import { AddEventButton } from '../../components/AddEventButton';
import { Enterprise, ENTERPRISE_COLLECTION, User, USER_COLLECTION, type MerchantV2 } from '../../type';
import { DialogMode } from '../../type/DialogMode';
import { collection, getFirestore, onSnapshot, orderBy, query } from 'firebase/firestore';
import { MerchantV2Table } from '../../components/MerchantV2Table';
import MerchantV2Dialog from '../../components/MerchantV2Dialog';

interface MerchantV2ContentProps {
  merchants: MerchantV2[];
  loading: boolean;
  error: string | null;
  addMerchant: (merchant: MerchantV2) => Promise<void>;
  updateMerchant: (merchant: MerchantV2) => Promise<void>;
  deleteMerchant: (merchantId: string) => Promise<void>;
}

const MerchantV2Content = ({
  merchants,
  loading,
  error,
  addMerchant,
  updateMerchant,
  deleteMerchant,
}: MerchantV2ContentProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogMode, setDialogMode] = useState<DialogMode>(DialogMode.CREATE);
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantV2 | undefined>(undefined);
  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);

  useEffect(() => {
    const db = getFirestore();
    const usersRef = collection(db, USER_COLLECTION);
    const q = query(usersRef, orderBy('createdAt', 'desc'));

    onSnapshot(q, (snapshot) => {
      const i_users: User[] = [];
      const e_users: User[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as User;
        if (data.uid?.length > 0 && data.displayName?.length > 0) {
          if (data.isEnterpriseUser && data.enterpriseId) {
            e_users.push(data);
          } else {
            i_users.push(data);
          }
        }
      });

      setIndependentUsers(i_users);
      setEnterpriseUsers(e_users);
    });

    const enterprisesRef = collection(db, ENTERPRISE_COLLECTION);
    const eq = query(enterprisesRef, orderBy('createdAt', 'desc'));

    onSnapshot(eq, (snapshot) => {
      const list: Enterprise[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as Enterprise;
        list.push(data);
      });

      setEnterprises(list);
    });
  }, []);

  const handleAddBoard = () => {
    setDialogMode(DialogMode.CREATE);
    setSelectedMerchant(undefined);
    setIsDialogOpen(true);
  };

  const handleEditMerchant = (merchant: MerchantV2) => {
    setDialogMode(DialogMode.EDIT);
    setSelectedMerchant(merchant);
    setIsDialogOpen(true);
  };

  const handleSaveMerchant = async (merchant: MerchantV2) => {
    try {
      if (dialogMode === DialogMode.EDIT) {
        await updateMerchant(merchant);
      } else {
        await addMerchant(merchant);
      }
      handleCloseDialog();
    } catch (error) {
      alert('상점 저장에 실패했습니다.');
    }
  };

  const handleDeleteMerchant = async (merchantId: string) => {
    try {
      const confirmed = window.confirm('정말로 이 상점을 삭제하시겠습니까?');
      if (confirmed) {
        await deleteMerchant(merchantId);
        handleCloseDialog();
      }
    } catch (error) {
      alert('상점 삭제에 실패했습니다.');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedMerchant(undefined);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <AnimatedBox>
        <PageTitle>상점 관리 V2</PageTitle>
        <LoadingState />
      </AnimatedBox>
    );
  }

  return (
    <AnimatedBox>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <PageTitle>상점 관리 V2</PageTitle>
        <AddEventButton title='새 상점 추가' onAddClick={handleAddBoard} />
      </Box>

      {error && (
        <Box sx={{ mb: 3, color: 'error.main' }}>
          {error}
        </Box>
      )}

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="상점 목록" />
      </Tabs>

      {activeTab === 0 && (
        // 상점 목록 탭
        <Box>
          {merchants.length === 0 ? (
            <EmptyState message="아직 등록된 상점이 없습니다." />
          ) : (
            <MerchantV2Table
              data={merchants}
              onMerchantSelect={handleEditMerchant}
            />
          )}
        </Box>
      )}

      {isDialogOpen && (
        <MerchantV2Dialog
          open={isDialogOpen}
          mode={dialogMode}
          merchant={selectedMerchant}
          onClose={handleCloseDialog}
          onSave={handleSaveMerchant}
          onDelete={handleDeleteMerchant}
          i_users={independentUsers}
          e_users={enterpriseUsers}
          ent={enterprises}
        />
      )}
    </AnimatedBox>
  );
};

export default MerchantV2Content;
